/** Deliver header/native create intents after the shared Tasks inspector has mounted its listeners. */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { TASK_QUEUE_READY_EVENT, TASK_QUEUE_REQUEST_EVENT } from './calendarTaskQueue';

export function useCalendarTaskCreateIntent(currentView: string, panelVisible: boolean, openPanel: () => void): void {
  const current = useRef({ currentView, panelVisible, openPanel });
  current.current = { currentView, panelVisible, openPanel };
  const pending = useRef(false);
  const ready = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    if (!panelVisible || currentView !== 'calendar') {
      pending.current = false;
      ready.current = false;
      generation.current += 1;
    } else {
      window.dispatchEvent(new CustomEvent(TASK_QUEUE_REQUEST_EVENT));
    }
  }, [currentView, panelVisible]);

  useLayoutEffect(() => {
    const createInPanel = () => window.dispatchEvent(new CustomEvent('tasks:create-new'));
    const onCreate = (event: Event) => {
      const state = current.current;
      if (state.currentView !== 'calendar') return;
      if (state.panelVisible && ready.current) {
        if (event.type === 'calendar:create-task') createInPanel();
        return;
      }
      event.stopImmediatePropagation();
      if (pending.current) return;
      pending.current = true;
      state.openPanel();
    };
    const onReady = () => {
      if (!current.current.panelVisible || current.current.currentView !== 'calendar') return;
      ready.current = true;
      if (!pending.current) return;
      pending.current = false;
      const version = generation.current;
      queueMicrotask(() => {
        if (version === generation.current && current.current.panelVisible && current.current.currentView === 'calendar') createInPanel();
      });
    };
    window.addEventListener('calendar:create-task', onCreate, true);
    window.addEventListener('tasks:create-new', onCreate, true);
    window.addEventListener(TASK_QUEUE_READY_EVENT, onReady);
    return () => {
      window.removeEventListener('calendar:create-task', onCreate, true);
      window.removeEventListener('tasks:create-new', onCreate, true);
      window.removeEventListener(TASK_QUEUE_READY_EVENT, onReady);
      generation.current += 1;
    };
  }, []);
}
